import { Suspense, useEffect, useMemo, useRef, type ReactNode } from 'react';
import { BrowserRouter, Link, Route, Routes } from 'react-router';
import { QueryClientProvider } from '@tanstack/react-query';
import { Button } from '@mt/ui';
import { AuthProvider, useAuth } from '../auth/AuthProvider.tsx';
import { ForgotPasswordPage, ResetPasswordPage, SignInPage, SignUpPage } from '../auth/AuthPages.tsx';
import { GuestOnly, PublicOnly, RequireAuth, RequireOnboarded, RootRedirect, Splash } from '../auth/guards.tsx';
import { AppShell } from '../layout/AppShell.tsx';
import { createQueryClient } from '../lib/queries.ts';
import { ToastProvider } from '../components/toast.tsx';
import { FullScreenMessage } from '../components/common.tsx';
import { PreferencesSync } from './PreferencesSync.tsx';
import { lazyPage } from './resilience.tsx';

const LandingPage = lazyPage(() => import('../guest/LandingPage.tsx'));
const TryPage = lazyPage(() => import('../guest/TryPage.tsx'));
const GuestPlayPage = lazyPage(() => import('../guest/GuestPlayPage.tsx'));
const OnboardingPage = lazyPage(() => import('../onboarding/OnboardingPage.tsx'));
const HomePage = lazyPage(() => import('../student/HomePage.tsx'));
const PathPage = lazyPage(() => import('../student/PathPage.tsx'));
const LessonPage = lazyPage(() => import('../student/LessonPage.tsx'));
const PracticePage = lazyPage(() => import('../student/PracticePage.tsx'));
const ClassesPage = lazyPage(() => import('../student/ClassesPage.tsx'));
const ClassDetailPage = lazyPage(() => import('../student/ClassDetailPage.tsx'));
const ProgressPage = lazyPage(() => import('../student/ProgressPage.tsx'));
const LeaderboardPage = lazyPage(() => import('../student/LeaderboardPage.tsx'));
const StartSessionPage = lazyPage(() => import('../player/StartSessionPage.tsx'));
const SessionPage = lazyPage(() => import('../player/SessionPage.tsx'));
const PreviewPage = lazyPage(() => import('../player/PreviewPage.tsx'));
const SettingsPage = lazyPage(() => import('../settings/SettingsPage.tsx'));
const TeacherDashboardPage = lazyPage(() => import('../teacher/TeacherDashboardPage.tsx'));
const NewClassroomPage = lazyPage(() => import('../teacher/NewClassroomPage.tsx'));
const ClassroomPage = lazyPage(() => import('../teacher/ClassroomPage.tsx'));
const PlanEditorPage = lazyPage(() => import('../teacher/PlanEditorPage.tsx'));
const AssignmentReportPage = lazyPage(() => import('../teacher/AssignmentReportPage.tsx'));
const StudentReportPage = lazyPage(() => import('../teacher/StudentReportPage.tsx'));
const TemplatesPage = lazyPage(() => import('../teacher/TemplatesPage.tsx'));

function Lazy({ children }: { children: ReactNode }) {
  return <Suspense fallback={<Splash />}>{children}</Suspense>;
}

function NotFound() {
  return (
    <FullScreenMessage
      title="Page not found"
      action={
        <Link to="/">
          <Button>Go home</Button>
        </Link>
      }
    >
      That page doesn’t exist or has moved.
    </FullScreenMessage>
  );
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<RootRedirect signedOut={<Lazy><LandingPage /></Lazy>} />} />
      <Route element={<GuestOnly />}>
        <Route path="/try" element={<Lazy><TryPage /></Lazy>} />
        <Route path="/try/play" element={<Lazy><GuestPlayPage /></Lazy>} />
      </Route>
      <Route element={<PublicOnly />}>
        <Route path="/signin" element={<SignInPage />} />
        <Route path="/signup" element={<SignUpPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      </Route>
      <Route path="/reset-password" element={<ResetPasswordPage />} />

      <Route element={<RequireAuth />}>
        <Route path="/onboarding" element={<Lazy><OnboardingPage /></Lazy>} />
        <Route element={<RequireOnboarded />}>
          <Route path="/start" element={<Lazy><StartSessionPage /></Lazy>} />
          <Route path="/session/:sessionId" element={<Lazy><SessionPage /></Lazy>} />
          <Route path="/teach/preview" element={<Lazy><PreviewPage /></Lazy>} />

          <Route element={<AppShell workspace="student" />}>
            <Route path="/home" element={<Lazy><HomePage /></Lazy>} />
            <Route path="/path" element={<Lazy><PathPage /></Lazy>} />
            <Route path="/path/:lessonId" element={<Lazy><LessonPage /></Lazy>} />
            <Route path="/practice" element={<Lazy><PracticePage /></Lazy>} />
            <Route path="/classes" element={<Lazy><ClassesPage /></Lazy>} />
            <Route path="/classes/:classroomId" element={<Lazy><ClassDetailPage /></Lazy>} />
            <Route path="/progress" element={<Lazy><ProgressPage /></Lazy>} />
            <Route path="/leaderboard" element={<Lazy><LeaderboardPage /></Lazy>} />
            <Route path="/settings" element={<Lazy><SettingsPage /></Lazy>} />
          </Route>

          <Route element={<AppShell workspace="teacher" />}>
            <Route path="/teach" element={<Lazy><TeacherDashboardPage /></Lazy>} />
            <Route path="/teach/classes/new" element={<Lazy><NewClassroomPage /></Lazy>} />
            <Route path="/teach/classes/:classroomId" element={<Lazy><ClassroomPage /></Lazy>} />
            <Route path="/teach/classes/:classroomId/students/:studentId" element={<Lazy><StudentReportPage /></Lazy>} />
            <Route path="/teach/plans/:planId" element={<Lazy><PlanEditorPage /></Lazy>} />
            <Route path="/teach/assignments/:assignmentId" element={<Lazy><AssignmentReportPage /></Lazy>} />
            <Route path="/teach/templates" element={<Lazy><TemplatesPage /></Lazy>} />
            <Route path="/teach/settings" element={<Lazy><SettingsPage /></Lazy>} />
          </Route>
        </Route>
      </Route>
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

function Providers() {
  // The auth provider needs the query client to clear caches on account change,
  // and the query client needs to report expired sessions back to auth.
  const expireRef = useRef<() => void>(() => {});
  const queryClient = useMemo(() => createQueryClient(() => expireRef.current()), []);
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <ExpireBridge setExpire={(fn) => (expireRef.current = fn)} />
        <PreferencesSync />
        <ToastProvider>
          <AppRoutes />
        </ToastProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}

function ExpireBridge({ setExpire }: { setExpire: (fn: () => void) => void }) {
  const { markExpired, session } = useAuth();
  useEffect(() => {
    setExpire(() => {
      if (session) markExpired();
    });
  }, [markExpired, session, setExpire]);
  return null;
}

export function App() {
  return (
    <BrowserRouter>
      <Providers />
    </BrowserRouter>
  );
}
